import { useQuery } from '@apollo/client/react';
import { useRouter } from 'expo-router';
import { memo, useCallback, useMemo } from 'react';
import { FlatList, RefreshControl, View } from 'react-native';
import { Divider, Text } from 'react-native-paper';

import { ErrorState, LoadingState } from '@/components/atoms/state';
import { PaginationFooter } from '@/components/molecules/list/PaginationFooter';
import { GetContactsDocument, type GetContactsQuery } from '@/generated/usermobile-bff/graphql';
import { usePaginatedFetchMore, useRefreshHandler } from '@/hooks';
import { useTranslation } from '@/i18n';
import { useAppTheme } from '@/theme';

import { ContactListItem } from './ContactListItem';

const PER_PAGE = 20;
const ORDER_BY = 'last_action_occurred_at desc';
interface Props {
  searchQuery?: string;
  ListHeaderComponent?: React.ComponentType<any> | React.ReactElement | null;
  onPress?: (id: number) => void;
}

const Separator = () => <Divider />;

const ContactListRow = memo(
  ({
    item,
    onPress,
  }: {
    item: GetContactsQuery['contacts']['data'][number];
    onPress: (id: number) => void;
  }) => (
    <ContactListItem
      id={item.id}
      displayName={item.displayName}
      email={item.email}
      personalMobileNumber={item.personalMobileNumber}
      onPress={onPress}
    />
  ),
);
ContactListRow.displayName = 'ContactListRow';

export const ContactList = ({ searchQuery, ListHeaderComponent, onPress }: Props) => {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const router = useRouter();
  const { data, loading, error, refetch, fetchMore, networkStatus } = useQuery(
    GetContactsDocument,
    {
      variables: {
        input: {
          page: 1,
          perPage: PER_PAGE,
          search: searchQuery || undefined,
          orderBy: ORDER_BY,
        },
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  );

  const buildVariables = useMemo(
    () => (nextPage: number) => ({
      input: {
        page: nextPage,
        perPage: PER_PAGE,
        search: searchQuery || undefined,
        orderBy: ORDER_BY,
      },
    }),
    [searchQuery],
  );

  const { isFetchingMore, handleEndReached } = usePaginatedFetchMore<GetContactsQuery>({
    networkStatus,
    data,
    entityKey: 'contacts',
    fetchMore,
    buildVariables,
  });

  const { onRefresh, refreshing } = useRefreshHandler(refetch);

  const handlePress = useCallback(
    (id: number) => {
      if (onPress) {
        onPress(id);
        return;
      }

      router.push(`/contact/${id}`);
    },
    [router, onPress],
  );

  const renderItem = useCallback(
    ({ item }: { item: GetContactsQuery['contacts']['data'][number] }) => (
      <ContactListRow item={item} onPress={handlePress} />
    ),
    [handlePress],
  );

  if (loading && !data) {
    return <LoadingState />;
  }

  if (error && !data) {
    return <ErrorState message={t('contacts.loadError')} />;
  }

  return (
    <FlatList
      data={data?.contacts.data ?? []}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      onEndReached={handleEndReached}
      onEndReachedThreshold={0.5}
      ListEmptyComponent={
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}>
          <Text variant="bodyLarge">{t('contacts.empty')}</Text>
        </View>
      }
      ListFooterComponent={<PaginationFooter isFetchingMore={isFetchingMore} />}
      ItemSeparatorComponent={Separator}
      ListHeaderComponent={
        typeof ListHeaderComponent === 'function' ? <ListHeaderComponent /> : ListHeaderComponent
      }
      refreshControl={
        <RefreshControl
          colors={[theme.colors.iconNdPrimary]}
          refreshing={refreshing}
          testID="contact-list-refresh-control"
          tintColor={theme.colors.iconNdPrimary}
          onRefresh={onRefresh}
        />
      }
      testID="contact-list"
    />
  );
};

const keyExtractor = (item: GetContactsQuery['contacts']['data'][number]) => String(item.id);
